'use client';

import { Suspense, useState } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Eye, EyeOff, Shield } from 'lucide-react';

function LoginForm() {
  const router = useRouter();
  const params = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(
    params.get('error') === 'forbidden'
      ? 'This login is for InstenOne admins only.'
      : ''
  );
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !password) {
      setError('Enter email and password.');
      return;
    }
    if (!cleanEmail.includes('@') || cleanEmail.split('@').length !== 2) {
      setError('Enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');
    const result = await signIn('credentials', {
      email: cleanEmail,
      password,
      redirect: false,
    });
    setLoading(false);

    if (result?.error || !result?.ok) {
      setError('That email or password isn’t right, or this account can’t sign in here.');
      return;
    }

    router.push(params.get('callbackUrl') || '/');
    router.refresh();
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-brand-950 text-white">
      {/* Atmosphere */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 10% 20%, rgba(15,118,110,0.35), transparent 55%), radial-gradient(ellipse 60% 40% at 90% 80%, rgba(20,184,166,0.12), transparent 50%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col lg:flex-row lg:items-stretch">
        {/* Brand panel */}
        <aside className="flex flex-1 flex-col justify-between px-8 py-10 lg:px-14 lg:py-16">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-600 text-[13px] font-extrabold tracking-tight">
              IO
            </div>
            <div>
              <div className="text-[15px] font-extrabold tracking-tight">InstenOne</div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/40">
                Control center
              </div>
            </div>
          </div>

          <div className="mt-12 max-w-md lg:mt-0">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold text-white/70">
              <Shield size={12} className="text-brand-400" />
              Super admin only
            </div>
            <h1 className="text-[34px] font-bold leading-[1.1] tracking-tight sm:text-[40px]">
              One place to look after every shop.
            </h1>
            <p className="mt-4 text-[15px] leading-relaxed text-white/55">
              Partners, products, money, and support — the same network your shops already use.
            </p>
          </div>

          <p className="mt-10 hidden text-[12px] text-white/30 lg:block">
            Restricted access · Audit logged · Separate host
          </p>
        </aside>

        {/* Form panel */}
        <main className="flex flex-1 items-center justify-center px-6 pb-12 lg:px-10 lg:py-16">
          <div className="w-full max-w-[420px] rounded-2xl border border-white/10 bg-[#062524]/90 p-7 sm:p-8">
            <div className="mb-7">
              <h2 className="text-[20px] font-extrabold tracking-tight">Sign in</h2>
              <p className="mt-1 text-[13px] text-white/45">
                Use the email and password for this admin.
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="mb-5 rounded-xl border border-red-500/25 bg-red-500/10 px-3.5 py-3 text-[13px] leading-snug text-red-200"
              >
                {error}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor="admin-email" className="mb-1.5 block text-[12px] font-bold text-white/70">
                  Email
                </label>
                <input
                  id="admin-email"
                  name="email"
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="admin@instentech.in"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={loading}
                  className="w-full rounded-xl border border-white/12 bg-[#0a3a38] px-3.5 py-3 text-[14px] text-white outline-none transition placeholder:text-white/30 focus:border-brand-400 focus:ring-2 focus:ring-brand-500/30 disabled:opacity-60"
                />
              </div>

              <div>
                <label
                  htmlFor="admin-password"
                  className="mb-1.5 block text-[12px] font-bold text-white/70"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="admin-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={loading}
                    className="w-full rounded-xl border border-white/12 bg-[#0a3a38] px-3.5 py-3 pr-11 text-[14px] text-white outline-none transition placeholder:text-white/30 focus:border-brand-400 focus:ring-2 focus:ring-brand-500/30 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-2.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-white/45 hover:bg-white/5 hover:text-white"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 flex w-full cursor-pointer items-center justify-center rounded-xl bg-brand-600 px-4 py-3 text-[14px] font-bold text-white transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Signing in…' : 'Sign in to console'}
              </button>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-brand-950 text-white/50">
          Loading…
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
